import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Sparkles, Filter, ChevronDown, MapPin, Building2, Users, AlertTriangle, Search, Check, Globe, TrendingUp, Zap, BarChart3, LineChart as LineChartIcon, Shield, CloudRain, FileWarning, ChevronUp, X, Calendar, Bookmark, LayoutGrid, Trash2, Plus } from 'lucide-react';
import { ENRICHED_MOCK_RISK_AREAS } from '../../data/mockPreventionData';
import { PROJECT_REGION_MAP, REGIONS } from '../../data/regionData';
import { RiskFactorScores, FactorSeverity } from '../../types/prevention';
// --- Types & Constants ---
type TimeFrame = '7d' | '14d' | '30d' | '90d' | '1y' | 'custom';
interface SavedWidget {
  id: string;
  name: string;
  filters: {
    timeFrame: TimeFrame;
    customStartDate?: string;
    customEndDate?: string;
    company: boolean;
    regions: string[];
    projects: string[];
    contractors: string[];
    factors: string[];
  };
  createdAt: string;
}
interface SubFactor {
  id: string;
  label: string;
  mapFromFactor: keyof RiskFactorScores | 'overall';
}
interface Category {
  id: string;
  label: string;
  factors: SubFactor[];
}
const CATEGORIES: Category[] = [{
  id: 'general',
  label: 'General',
  factors: [{
    id: 'overallRiskScore',
    label: 'Overall Risk Score',
    mapFromFactor: 'overall'
  }]
}, {
  id: 'planningControls',
  label: 'Safety Planning & Controls',
  factors: [{
    id: 'ptpQuality',
    label: 'PTP Quality',
    mapFromFactor: 'ptpQuality'
  }, {
    id: 'ptpEngagement',
    label: 'PTP Engagement',
    mapFromFactor: 'ptpEngagement'
  }, {
    id: 'jhaQuality',
    label: 'JHA Quality',
    mapFromFactor: 'documents'
  }, {
    id: 'toolboxEngagement',
    label: 'Toolbox Engagement',
    mapFromFactor: 'ptpEngagement'
  }, {
    id: 'controlsValidation',
    label: 'Controls Validation',
    mapFromFactor: 'inspections'
  }, {
    id: 'ppeValidation',
    label: 'PPE Validation',
    mapFromFactor: 'inspections'
  }, {
    id: 'sdsCompliance',
    label: 'SDS Compliance & Engagement',
    mapFromFactor: 'documents'
  }]
}, {
  id: 'observationsInspections',
  label: 'Observations & Inspections',
  factors: [{
    id: 'observationFrequency',
    label: 'Observation Frequency',
    mapFromFactor: 'inspections'
  }, {
    id: 'observationSeverity',
    label: 'Observation Severity',
    mapFromFactor: 'incidents'
  }, {
    id: 'openObservationCount',
    label: 'Open Observation Count',
    mapFromFactor: 'incidents'
  }, {
    id: 'positiveObservationRatio',
    label: 'Positive Observation Ratio',
    mapFromFactor: 'inspections'
  }, {
    id: 'inspectionCompliance',
    label: 'Inspection Compliance',
    mapFromFactor: 'inspections'
  }, {
    id: 'reinspectionRate',
    label: 'Reinspection Rate',
    mapFromFactor: 'inspections'
  }]
}, {
  id: 'workforceContractors',
  label: 'Workforce & Contractors',
  factors: [{
    id: 'workerCompetency',
    label: 'Worker Competency',
    mapFromFactor: 'workers'
  }, {
    id: 'newWorkerRatio',
    label: 'New Worker Ratio',
    mapFromFactor: 'workers'
  }, {
    id: 'fatigueRisk',
    label: 'Fatigue Risk',
    mapFromFactor: 'workers'
  }, {
    id: 'crewStability',
    label: 'Crew Stability',
    mapFromFactor: 'workers'
  }, {
    id: 'supervisionRatio',
    label: 'Supervision Ratio',
    mapFromFactor: 'workers'
  }, {
    id: 'contractorHistory',
    label: 'Contractor Records',
    mapFromFactor: 'workers'
  }, {
    id: 'contractorObservations',
    label: 'Contractor Observations & Inspections',
    mapFromFactor: 'inspections'
  }]
}, {
  id: 'incidentsLoopClosure',
  label: 'Incidents & Loop Closure',
  factors: [{
    id: 'openIncidents',
    label: 'Open Incident Count',
    mapFromFactor: 'incidents'
  }, {
    id: 'incidentRecurrence',
    label: 'Incident Recurrence',
    mapFromFactor: 'incidents'
  }, {
    id: 'nearMissRatio',
    label: 'Near-Miss Ratio',
    mapFromFactor: 'incidents'
  }, {
    id: 'correctiveActionClosure',
    label: 'Corrective Action Closure',
    mapFromFactor: 'incidents'
  }]
}, {
  id: 'inherentRiskEnvironment',
  label: 'Inherent Risk & Environment',
  factors: [{
    id: 'projectType',
    label: 'Project Type',
    mapFromFactor: 'weather'
  }, {
    id: 'projectPhase',
    label: 'Project Phase',
    mapFromFactor: 'weather'
  }, {
    id: 'weatherSeverity',
    label: 'Weather Severity',
    mapFromFactor: 'weather'
  }, {
    id: 'siteCongestion',
    label: 'Site Congestion',
    mapFromFactor: 'weather'
  }, {
    id: 'hazardousMaterials',
    label: 'Hazardous Materials',
    mapFromFactor: 'weather'
  }, {
    id: 'heavyEquipmentDensity',
    label: 'Heavy Equipment Density',
    mapFromFactor: 'weather'
  }]
}, {
  id: 'safetyCultureEngagement',
  label: 'Safety Culture & Engagement',
  factors: [{
    id: 'safetyCultureIndex',
    label: 'Safety Culture Index',
    mapFromFactor: 'workers'
  }]
}];
const ALL_FACTORS = CATEGORIES.flatMap(c => c.factors);
const CHART_COLORS = ['#0ea5e9', '#8b5cf6', '#f43f5e', '#f59e0b', '#10b981', '#6366f1', '#ec4899', '#14b8a6', '#84cc16', '#f97316' // orange-500
];
type EntityType = 'company' | 'region' | 'project' | 'contractor';
interface Entity {
  type: EntityType;
  id: string;
  name: string;
}
// --- Helper Functions ---
function createSeededRandom(seed: number) {
  return function () {
    let t = seed += 0x6d2b79f5;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}
function hashCode(str: string) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return hash;
}
const generateFactorTimeSeries = (seedString: string, mappedSeverity: FactorSeverity, days: number = 365) => {
  const seed = hashCode(seedString);
  const random = createSeededRandom(seed);
  let baseMin = 50,
    baseMax = 60;
  if (mappedSeverity === 'high') {
    baseMin = 80;
    baseMax = 95;
  } else if (mappedSeverity === 'medium') {
    baseMin = 65;
    baseMax = 80;
  } else if (mappedSeverity === 'low') {
    baseMin = 55;
    baseMax = 70;
  }
  let currentScore = baseMin + random() * (baseMax - baseMin);
  const trendBias = (random() - 0.5) * 0.5;
  const history = [];
  for (let i = days; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    date.setHours(0, 0, 0, 0);
    currentScore += (random() - 0.5) * 4 + trendBias;
    currentScore = Math.max(50, Math.min(100, currentScore));
    history.push({
      date: date.toISOString().split('T')[0],
      score: currentScore
    });
  }
  return history;
};
// --- Multi-Select Dropdown Component ---
interface MultiSelectProps {
  icon: React.ReactNode;
  label: string;
  options: {
    value: string;
    label: string;
    group?: string;
  }[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder: string;
}
function MultiSelect({
  icon,
  label,
  options,
  selected,
  onChange,
  placeholder
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const filteredOptions = options.filter(opt => opt.label.toLowerCase().includes(search.toLowerCase()) || opt.group && opt.group.toLowerCase().includes(search.toLowerCase()));
  const groupedOptions = filteredOptions.reduce((acc, opt) => {
    const group = opt.group || 'Other';
    if (!acc[group]) acc[group] = [];
    acc[group].push(opt);
    return acc;
  }, {} as Record<string, typeof options>);
  const hasGroups = Object.keys(groupedOptions).length > 1 || Object.keys(groupedOptions).length === 1 && Object.keys(groupedOptions)[0] !== 'Other';
  const handleSelectAll = () => {
    if (selected.length === options.length) {
      onChange([]);
    } else {
      onChange(options.map(o => o.value));
    }
  };
  return <div className="relative" ref={ref} data-id="element-1065" data-mp="awu9q1n63uo9">
      <button onClick={() => setIsOpen(!isOpen)} className={`flex items-center gap-2 px-3 py-1.5 bg-white border rounded-lg shadow-sm transition-colors ${isOpen ? 'border-teal-500 ring-1 ring-teal-500' : 'border-gray-200 hover:border-teal-300'}`} data-id="element-1066" data-mp="avsv5n6y0q1s">
        <div className="text-gray-400" data-id="element-1067" data-mp="ps0igwdne7la">{icon}</div>
        <span className="text-sm font-medium text-gray-700 whitespace-nowrap" data-id="element-1068" data-mp="2mlljdt1p52ws">
          {selected.length === 0 ? placeholder : selected.length === options.length ? `All ${label}` : `${selected.length} ${label}`}
        </span>
        <ChevronDown size={14} className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-1069" data-mp="3q8f8m4d161qv" />
      </button>

      <AnimatePresence data-id="element-1070" data-mp="30l8ub4rfh91">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: 4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 4
      }} transition={{
        duration: 0.15
      }} className="absolute z-50 top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden flex flex-col max-h-[400px]" data-id="element-1071" data-mp="33zbzll8zuhj">
            <div className="p-2 border-b border-gray-100" data-id="element-1072" data-mp="vqbrmxhc77bh">
              <div className="relative" data-id="element-1073" data-mp="vrv2u9o4ys6e">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1074" data-mp="10p54cnosuual" />
                <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-8 pr-3 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" data-id="element-1075" data-mp="1auwij1cygqy4" />
              </div>
            </div>

            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50/50" data-id="element-1076" data-mp="1tlnumaobuvg7">
              <span className="text-xs font-medium text-gray-500" data-id="element-1077" data-mp="1jtrtsruuwr8a">
                {selected.length} selected
              </span>
              <button onClick={handleSelectAll} className="text-xs font-medium text-teal-600 hover:text-teal-700" data-id="element-1078" data-mp="1frdbq9z0hvi2">
                {selected.length === options.length ? 'Clear All' : 'Select All'}
              </button>
            </div>

            <div className="overflow-y-auto p-1" data-id="element-1079" data-mp="5mk70b07md8s">
              {hasGroups ? Object.entries(groupedOptions).map(([group, opts]) => <div key={group} className="mb-2 last:mb-0" data-id="element-1080" data-mp="3s26fsplpuczx">
                      <div className="px-2 py-1 text-[11px] font-bold text-gray-400 uppercase tracking-wider sticky top-0 bg-white/90 backdrop-blur-sm z-10" data-id="element-1081" data-mp="3rzsjyej7owrb">
                        {group}
                      </div>
                      {opts.map(opt => <label key={opt.value} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded-md cursor-pointer group" data-id="element-1082" data-mp="3o0t624fi5pnq">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${selected.includes(opt.value) ? 'bg-teal-500 border-teal-500' : 'border-gray-300 group-hover:border-teal-400'}`} data-id="element-1083" data-mp="3e3y9hr2shzc3">
                            {selected.includes(opt.value) && <Check size={12} className="text-white" data-id="element-1084" data-mp="2ru810l54fmtb" />}
                          </div>
                          <span className="text-sm text-gray-700 truncate" data-id="element-1085" data-mp="36jxe9eqhnh9y">
                            {opt.label}
                          </span>
                          <input type="checkbox" className="hidden" checked={selected.includes(opt.value)} onChange={() => {
                if (selected.includes(opt.value)) onChange(selected.filter(v => v !== opt.value));else onChange([...selected, opt.value]);
              }} data-id="element-1086" data-mp="3fir8wzk9dxkv" />
                        </label>)}
                    </div>) : filteredOptions.map(opt => <label key={opt.value} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded-md cursor-pointer group" data-id="element-1087" data-mp="1wjbedcsa0bkg">
                      <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${selected.includes(opt.value) ? 'bg-teal-500 border-teal-500' : 'border-gray-300 group-hover:border-teal-400'}`} data-id="element-1088" data-mp="26h7tsgs8xsod">
                        {selected.includes(opt.value) && <Check size={12} className="text-white" data-id="element-1089" data-mp="1ir3lvx80z6rk" />}
                      </div>
                      <span className="text-sm text-gray-700 truncate" data-id="element-1090" data-mp="q4iuwxvgowoj">
                        {opt.label}
                      </span>
                      <input type="checkbox" className="hidden" checked={selected.includes(opt.value)} onChange={() => {
              if (selected.includes(opt.value)) onChange(selected.filter(v => v !== opt.value));else onChange([...selected, opt.value]);
            }} data-id="element-1091" data-mp="v22l8w5fozwu" />
                    </label>)}
              {filteredOptions.length === 0 && <div className="p-4 text-center text-sm text-gray-500" data-id="element-1092" data-mp="2knj5w08suwz3">
                  No results found
                </div>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
// --- Main Component ---
export function AnalyticsView() {
  const allContractors = useMemo(() => {
    const set = new Set<string>();
    ENRICHED_MOCK_RISK_AREAS.forEach(area => area.contractors.forEach(c => set.add(c)));
    return Array.from(set).sort();
  }, []);
  const allProjects = useMemo(() => {
    const set = new Set<string>();
    ENRICHED_MOCK_RISK_AREAS.forEach(area => set.add(area.project));
    return Array.from(set).sort();
  }, []);
  // Suggested Insights
  const SUGGESTED_INSIGHTS = useMemo(() => [{
    id: 'regional-ptp-gap',
    icon: <MapPin size={18} data-id="element-1093" data-mp="1n3j9ulkaw4gy" />,
    title: 'Northeast PTP Scores Declining vs. Southeast',
    description: 'Northeast Pre-Task Planning quality dropped 12 pts over 90 days while Southeast improved. 4 of 12 NE projects now below the 65-score threshold — correlating with a 23% rise in open observations.',
    badge: '⚠ Action Needed',
    badgeColor: 'bg-rose-500/20 text-rose-300 border-rose-400/30',
    aiInsight: "The Northeast PTP decline is concentrated in projects with recent crew turnover (Court Square LIC, 99 Claremont Ave). Southeast's improvement coincides with their new toolbox talk program launched 6 weeks ago. Recommendation: Replicate the Southeast's structured PTP review process in Northeast projects — this could recover 8–10 points within 30 days based on Southeast's trajectory.",
    filters: {
      timeFrame: '90d' as TimeFrame,
      company: false,
      regions: ['northeast', 'southeast'],
      projects: [] as string[],
      contractors: [] as string[],
      factors: ['ptpQuality', 'ptpEngagement']
    }
  }, {
    id: 'hudson-yards-workforce',
    icon: <TrendingUp size={18} data-id="element-1094" data-mp="3vad2uy4hufcu" />,
    title: 'Hudson Yards: Fatigue Risk 18pts Above Company Avg',
    description: 'Worker fatigue risk at Hudson Yards Tower C spiked to 87 (company avg: 69) over the past 30 days. Competency scores also diverged — new crew onboarding may be a contributing factor.',
    badge: '🔴 Anomaly',
    badgeColor: 'bg-amber-500/20 text-amber-300 border-amber-400/30',
    aiInsight: 'Hudson Yards Tower C shows a fatigue risk score of 87 vs. the company average of 69 — the widest gap across all active projects. This spike began 3 weeks ago and correlates with extended shift schedules during the concrete pour phase. Worker competency also dropped 9 pts, suggesting new crew members are being onboarded under high-fatigue conditions. Recommendation: Review shift scheduling for the next 2 weeks and ensure new workers are paired with experienced crew leads.',
    filters: {
      timeFrame: '30d' as TimeFrame,
      company: true,
      regions: [] as string[],
      projects: ['Hudson Yards Tower C'],
      contractors: [] as string[],
      factors: ['workerCompetency', 'fatigueRisk']
    }
  }, {
    id: 'contractor-inspection',
    icon: <BarChart3 size={18} data-id="element-1095" data-mp="fh2i6tb8ruwa" />,
    title: 'Contractor Compliance Gap Widening',
    description: `Your top 3 contractors show a 22-point spread in inspection compliance scores. The lowest performer has declined steadily for 6 weeks while the others remain stable.`,
    badge: '📊 Benchmark',
    badgeColor: 'bg-sky-500/20 text-sky-300 border-sky-400/30',
    aiInsight: `Inspection compliance across your top 3 contractors reveals a growing performance gap. ${allContractors[0] || 'Contractor A'} leads at ~82, while ${allContractors[2] || 'Contractor C'} has dropped to ~60 — a 6-week downward trend. Findings severity for the lowest performer is also escalating, with 3x more critical findings than the top performer. Recommendation: Schedule a compliance review meeting with ${allContractors[2] || 'the lowest performer'} and consider increasing inspection frequency on their active projects until scores stabilize above 70.`,
    filters: {
      timeFrame: '90d' as TimeFrame,
      company: true,
      regions: [] as string[],
      projects: [] as string[],
      contractors: allContractors.slice(0, 3),
      factors: ['inspectionCompliance', 'reinspectionRate']
    }
  }, {
    id: 'regional-risk-score-divergence',
    icon: <Shield size={18} data-id="element-1096" data-mp="281cf1hkibkk" />,
    title: 'Regional Risk Scores Diverging Over 1 Year',
    description: 'Midwest and West Coast overall risk scores have diverged by 14 points over the past year. Midwest improved steadily while West Coast plateaued — signaling different operational maturity trajectories.',
    badge: '📈 Strategic',
    badgeColor: 'bg-violet-500/20 text-violet-300 border-violet-400/30',
    aiInsight: "Over the past year, Midwest projects reduced their average risk score from 78 to 64 — a 14-point improvement driven by consistent PTP adoption and lower crew turnover. West Coast projects remained flat at ~76, with gains in documentation offset by rising fatigue risk scores. The divergence accelerated 4 months ago when Midwest rolled out weekly safety stand-ups. Recommendation: Conduct a best-practice transfer session between Midwest and West Coast regional leads, focusing on the stand-up cadence and PTP review process that drove Midwest's improvement.",
    filters: {
      timeFrame: '1y' as TimeFrame,
      company: false,
      regions: ['midwest', 'west_coast'],
      projects: [] as string[],
      contractors: [] as string[],
      factors: ['overallRiskScore']
    }
  }, {
    id: 'weather-incident-correlation',
    icon: <CloudRain size={18} data-id="element-1097" data-mp="1q4ilghkdne09" />,
    title: 'Southeast: Weather Driving Incident Spikes',
    description: 'When weather severity scores exceed 80 in the Southeast, open incident counts spike within 5–7 days. This pattern repeated 4 times in the last 90 days — a predictable and preventable cycle.',
    badge: '🌧 Pattern',
    badgeColor: 'bg-cyan-500/20 text-cyan-300 border-cyan-400/30',
    aiInsight: 'Analysis of Southeast projects over 90 days reveals a strong leading indicator: weather severity scores above 80 precede incident spikes by 5–7 days with 85% consistency. The most affected projects are Midtown Atlanta Tower and Miami Brickell City Centre 2, where outdoor concrete and steel work is weather-sensitive. The company average shows a weaker correlation, suggesting this is a region-specific vulnerability. Recommendation: Implement automated weather-triggered safety protocols for Southeast projects — when weather severity crosses 75, pre-position additional safety supervisors and delay non-critical elevated work by 24 hours.',
    filters: {
      timeFrame: '90d' as TimeFrame,
      company: true,
      regions: ['southeast'],
      projects: [] as string[],
      contractors: [] as string[],
      factors: ['weatherSeverity', 'openIncidents']
    }
  }, {
    id: 'documentation-midatlantic-gap',
    icon: <FileWarning size={18} data-id="element-1098" data-mp="2eqign2e2jr7r" />,
    title: 'Mid-Atlantic: JHA & SDS Compliance Gaps Escalating',
    description: 'JHA quality dropped 11 points across Mid-Atlantic projects in 30 days. SDS compliance is also declining — 3 projects now below the 60-score compliance threshold.',
    badge: '🚨 Compliance',
    badgeColor: 'bg-red-500/20 text-red-300 border-red-400/30',
    aiInsight: 'Mid-Atlantic documentation scores are in rapid decline. JHA quality fell from 74 to 63 across the region in just 30 days, with Brooklyn Navy Yard and Penn Station Renovation leading the drop. SDS compliance shows a parallel decline, suggesting a systemic issue rather than isolated project failures. This coincides with a regional staffing change — the Mid-Atlantic safety coordinator role has been vacant for 3 weeks. Recommendation: Prioritize filling the coordinator role immediately. In the interim, assign a temporary safety lead from the Northeast region (which has the strongest documentation scores) to conduct weekly JHA audits across the 3 most affected Mid-Atlantic projects.',
    filters: {
      timeFrame: '30d' as TimeFrame,
      company: true,
      regions: ['mid_atlantic'],
      projects: [] as string[],
      contractors: [] as string[],
      factors: ['jhaQuality', 'sdsCompliance']
    }
  }], [allContractors]);
  // State
  const [timeFrame, setTimeFrame] = useState<TimeFrame>('30d');
  const [customStartDate, setCustomStartDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 60);
    return d.toISOString().split('T')[0];
  });
  const [customEndDate, setCustomEndDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const customDateRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (customDateRef.current && !customDateRef.current.contains(event.target as Node)) {
        setShowCustomDatePicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Entity Selection State
  const [selectedCompany, setSelectedCompany] = useState<boolean>(false);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [selectedContractors, setSelectedContractors] = useState<string[]>([]);
  // Factor Selection State
  const [selectedFactors, setSelectedFactors] = useState<string[]>([]);
  const [isFactorPanelExpanded, setIsFactorPanelExpanded] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [aiInsight, setAiInsight] = useState('');
  const [activeInsightId, setActiveInsightId] = useState<string | null>(null);
  // Legend highlight state
  const [hoveredLineKey, setHoveredLineKey] = useState<string | null>(null);
  const [pinnedLineKeys, setPinnedLineKeys] = useState<Set<string>>(new Set());
  const highlightedKeys = pinnedLineKeys.size > 0 ? pinnedLineKeys : hoveredLineKey ? new Set([hoveredLineKey]) : null;
  // Saved Widgets State
  const [savedWidgets, setSavedWidgets] = useState<SavedWidget[]>(() => {
    try {
      const stored = localStorage.getItem('analyticsWidgets');
      return stored ? JSON.parse(stored) : [];
    } catch (_e) {
      return [];
    }
  });
  const widgetsSectionRef = useRef<HTMLDivElement>(null);
  const [showSaveWidgetPopover, setShowSaveWidgetPopover] = useState(false);
  const [widgetName, setWidgetName] = useState('');
  const saveWidgetRef = useRef<HTMLDivElement>(null);
  // Persist widgets to localStorage
  useEffect(() => {
    localStorage.setItem('analyticsWidgets', JSON.stringify(savedWidgets));
  }, [savedWidgets]);
  // Click outside handler for save widget popover
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (saveWidgetRef.current && !saveWidgetRef.current.contains(event.target as Node)) {
        setShowSaveWidgetPopover(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Auto-suggest widget name based on current selection
  const suggestedWidgetName = useMemo(() => {
    const entityNames: string[] = [];
    if (selectedCompany) entityNames.push('Company');
    if (selectedRegions.length > 0) {
      if (selectedRegions.length === 1) {
        const r = REGIONS.find(r => r.id === selectedRegions[0]);
        if (r) entityNames.push(r.name);
      } else {
        entityNames.push(`${selectedRegions.length} Regions`);
      }
    }
    if (selectedProjects.length > 0) {
      if (selectedProjects.length === 1) {
        entityNames.push(selectedProjects[0]);
      } else {
        entityNames.push(`${selectedProjects.length} Projects`);
      }
    }
    if (selectedContractors.length > 0) {
      if (selectedContractors.length === 1) {
        entityNames.push(selectedContractors[0]);
      } else {
        entityNames.push(`${selectedContractors.length} Contractors`);
      }
    }
    const factorNames = selectedFactors.slice(0, 2).map(id => {
      if (id.startsWith('cat_')) {
        const categoryId = id.replace('cat_', '');
        const category = CATEGORIES.find(c => c.id === categoryId);
        return category?.label;
      }
      return ALL_FACTORS.find(f => f.id === id)?.label;
    }).filter(Boolean);
    const entityPart = entityNames.slice(0, 2).join(' vs ');
    const factorPart = factorNames.length > 0 ? factorNames.join(', ') : selectedFactors.length > 0 ? `${selectedFactors.length} factors` : '';
    if (entityPart && factorPart) {
      return `${entityPart} — ${factorPart}`;
    } else if (entityPart) {
      return entityPart;
    } else if (factorPart) {
      return factorPart;
    }
    return 'My View';
  }, [selectedCompany, selectedRegions, selectedProjects, selectedContractors, selectedFactors]);
  // Initialize widget name when popover opens
  useEffect(() => {
    if (showSaveWidgetPopover) {
      setWidgetName(suggestedWidgetName);
    }
  }, [showSaveWidgetPopover, suggestedWidgetName]);
  const handleSaveWidget = () => {
    const newWidget: SavedWidget = {
      id: Date.now().toString(),
      name: widgetName.trim() || suggestedWidgetName,
      filters: {
        timeFrame,
        customStartDate: timeFrame === 'custom' ? customStartDate : undefined,
        customEndDate: timeFrame === 'custom' ? customEndDate : undefined,
        company: selectedCompany,
        regions: [...selectedRegions],
        projects: [...selectedProjects],
        contractors: [...selectedContractors],
        factors: [...selectedFactors]
      },
      createdAt: new Date().toISOString()
    };
    setSavedWidgets([newWidget, ...savedWidgets]);
    setShowSaveWidgetPopover(false);
    setWidgetName('');
    // Scroll to widgets section after a brief delay for render
    setTimeout(() => {
      widgetsSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 100);
  };
  const handleLoadWidget = (widget: SavedWidget) => {
    setTimeFrame(widget.filters.timeFrame);
    if (widget.filters.customStartDate) setCustomStartDate(widget.filters.customStartDate);
    if (widget.filters.customEndDate) setCustomEndDate(widget.filters.customEndDate);
    setSelectedCompany(widget.filters.company);
    setSelectedRegions(widget.filters.regions);
    setSelectedProjects(widget.filters.projects);
    setSelectedContractors(widget.filters.contractors);
    setSelectedFactors(widget.filters.factors);
    setActiveInsightId(null);
  };
  const handleDeleteWidget = (widgetId: string) => {
    setSavedWidgets(savedWidgets.filter(w => w.id !== widgetId));
  };
  // Helper: toggle category selection
  const toggleCategory = (categoryId: string) => {
    const catKey = `cat_${categoryId}`;
    if (selectedFactors.includes(catKey)) {
      setSelectedFactors(selectedFactors.filter(f => f !== catKey));
    } else {
      setSelectedFactors([...selectedFactors, catKey]);
    }
  };
  // Helper: toggle individual factor
  const toggleFactor = (factorId: string) => {
    if (selectedFactors.includes(factorId)) {
      setSelectedFactors(selectedFactors.filter(f => f !== factorId));
    } else {
      setSelectedFactors([...selectedFactors, factorId]);
    }
  };
  // Helper: check if category is selected
  const isCategorySelected = (categoryId: string) => selectedFactors.includes(`cat_${categoryId}`);
  // Helper: count selected factors (individual + categories)
  const selectedFactorCount = selectedFactors.length;
  // Helper: clear all factor selections
  const clearAllFactors = () => setSelectedFactors([]);
  // Apply a suggested insight
  const applyInsight = (insight: (typeof SUGGESTED_INSIGHTS)[number]) => {
    const isAlreadyActive = activeInsightId === insight.id;
    if (isAlreadyActive) {
      // Deselect — reset to default
      setActiveInsightId(null);
      setTimeFrame('30d');
      setSelectedCompany(true);
      setSelectedRegions([]);
      setSelectedProjects(allProjects.length > 0 ? [allProjects[0]] : []);
      setSelectedContractors([]);
      setSelectedFactors(['observationFrequency', 'ptpQuality']);
      return;
    }
    setActiveInsightId(insight.id);
    setTimeFrame(insight.filters.timeFrame);
    setSelectedCompany(insight.filters.company);
    setSelectedRegions(insight.filters.regions);
    setSelectedProjects(insight.filters.projects);
    setSelectedContractors(insight.filters.contractors);
    setSelectedFactors(insight.filters.factors);
  };
  // AI Auto-select on mount
  useEffect(() => {
    // Select Company Average and a specific project for comparison
    setSelectedCompany(true);
    if (allProjects.length > 0) {
      setSelectedProjects([allProjects[0]]);
    }
    setSelectedFactors(['observationFrequency', 'ptpQuality']);
  }, [allProjects]);
  // --- Data Aggregation ---
  const chartData = useMemo(() => {
    let days: number;
    if (timeFrame === 'custom') {
      const start = new Date(customStartDate);
      const end = new Date(customEndDate);
      days = Math.max(1, Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));
    } else {
      days = timeFrame === '7d' ? 7 : timeFrame === '14d' ? 14 : timeFrame === '30d' ? 30 : timeFrame === '90d' ? 90 : 365;
    }
    // Build list of entities to plot
    const entities: Entity[] = [];
    if (selectedCompany) entities.push({
      type: 'company',
      id: 'company',
      name: 'Company Average'
    });
    selectedRegions.forEach(id => entities.push({
      type: 'region',
      id,
      name: REGIONS.find(r => r.id === id)?.name || id
    }));
    selectedProjects.forEach(id => entities.push({
      type: 'project',
      id,
      name: id
    }));
    selectedContractors.forEach(id => entities.push({
      type: 'contractor',
      id,
      name: id
    }));
    if (entities.length === 0 || selectedFactors.length === 0) return {
      data: [],
      lines: []
    };
    // Separate category selections from individual factor selections
    const categorySelections = selectedFactors.filter(f => f.startsWith('cat_'));
    const individualFactorSelections = selectedFactors.filter(f => !f.startsWith('cat_'));
    // Build a list of all individual factors we need to generate (for both individual selections and category aggregates)
    const allNeededFactorIds = new Set<string>(individualFactorSelections);
    categorySelections.forEach(catKey => {
      const categoryId = catKey.replace('cat_', '');
      const category = CATEGORIES.find(c => c.id === categoryId);
      if (category) {
        category.factors.forEach(f => allNeededFactorIds.add(f.id));
      }
    });
    // Generate base time series for every project/factor combination
    const projectFactorSeries = new Map<string, {
      date: string;
      score: number;
    }[]>();
    const projectFactorSeverities = new Map<string, FactorSeverity>();
    ENRICHED_MOCK_RISK_AREAS.forEach(area => {
      allNeededFactorIds.forEach(factorId => {
        const factorDef = ALL_FACTORS.find(f => f.id === factorId);
        if (!factorDef) return;
        const severity: FactorSeverity = factorDef.mapFromFactor === 'overall' ? area.riskScore >= 75 ? 'high' : area.riskScore >= 50 ? 'medium' : area.riskScore >= 25 ? 'low' : 'none' : area.riskFactorScores[factorDef.mapFromFactor];
        const key = `${area.project}_${factorId}`;
        const currentWorst = projectFactorSeverities.get(key) || 'none';
        const order = {
          high: 3,
          medium: 2,
          low: 1,
          none: 0
        };
        if (order[severity] > order[currentWorst]) {
          projectFactorSeverities.set(key, severity);
        }
      });
    });
    allProjects.forEach(project => {
      allNeededFactorIds.forEach(factorId => {
        const key = `${project}_${factorId}`;
        const severity = projectFactorSeverities.get(key) || 'none';
        projectFactorSeries.set(key, generateFactorTimeSeries(`${project}_${factorId}`, severity, 365));
      });
    });
    // Aggregate data
    const dateMap = new Map<string, Record<string, number>>();
    for (let i = days; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      d.setHours(0, 0, 0, 0);
      dateMap.set(d.toISOString().split('T')[0], {
        date: d.getTime()
      });
    }
    const lines: {
      key: string;
      name: string;
      color: string;
    }[] = [];
    let colorIndex = 0;
    entities.forEach(entity => {
      // Process individual factor selections
      individualFactorSelections.forEach(factorId => {
        const factorDef = ALL_FACTORS.find(f => f.id === factorId);
        if (!factorDef) return;
        const lineKey = `${entity.id}_${factorId}`;
        const lineName = `${entity.name} - ${factorDef.label}`;
        lines.push({
          key: lineKey,
          name: lineName,
          color: CHART_COLORS[colorIndex % CHART_COLORS.length]
        });
        colorIndex++;
        // Determine which projects belong to this entity
        let relevantProjects: string[] = [];
        if (entity.type === 'company') {
          relevantProjects = allProjects;
        } else if (entity.type === 'region') {
          relevantProjects = PROJECT_REGION_MAP.filter(p => p.regionId === entity.id).map(p => p.projectName);
        } else if (entity.type === 'project') {
          relevantProjects = [entity.id];
        } else if (entity.type === 'contractor') {
          relevantProjects = Array.from(new Set(ENRICHED_MOCK_RISK_AREAS.filter(a => a.contractors.includes(entity.id)).map(a => a.project)));
        }
        Array.from(dateMap.keys()).forEach(dateStr => {
          const dataPoint = dateMap.get(dateStr)!;
          let sum = 0;
          let count = 0;
          relevantProjects.forEach(project => {
            const series = projectFactorSeries.get(`${project}_${factorId}`);
            if (series) {
              const point = series.find(p => p.date === dateStr);
              if (point) {
                sum += point.score;
                count++;
              }
            }
          });
          if (count > 0) {
            dataPoint[lineKey] = Math.round(sum / count * 10) / 10;
          }
        });
      });
      // Process category selections (aggregate trendlines)
      categorySelections.forEach(catKey => {
        const categoryId = catKey.replace('cat_', '');
        const category = CATEGORIES.find(c => c.id === categoryId);
        if (!category) return;
        const lineKey = `${entity.id}_${catKey}`;
        const lineName = `${entity.name} - ${category.label}`;
        lines.push({
          key: lineKey,
          name: lineName,
          color: CHART_COLORS[colorIndex % CHART_COLORS.length]
        });
        colorIndex++;
        // Determine which projects belong to this entity
        let relevantProjects: string[] = [];
        if (entity.type === 'company') {
          relevantProjects = allProjects;
        } else if (entity.type === 'region') {
          relevantProjects = PROJECT_REGION_MAP.filter(p => p.regionId === entity.id).map(p => p.projectName);
        } else if (entity.type === 'project') {
          relevantProjects = [entity.id];
        } else if (entity.type === 'contractor') {
          relevantProjects = Array.from(new Set(ENRICHED_MOCK_RISK_AREAS.filter(a => a.contractors.includes(entity.id)).map(a => a.project)));
        }
        // For each date, average all sub-factors in this category
        Array.from(dateMap.keys()).forEach(dateStr => {
          const dataPoint = dateMap.get(dateStr)!;
          let categorySum = 0;
          let categoryCount = 0;
          category.factors.forEach(subFactor => {
            let factorSum = 0;
            let factorCount = 0;
            relevantProjects.forEach(project => {
              const series = projectFactorSeries.get(`${project}_${subFactor.id}`);
              if (series) {
                const point = series.find(p => p.date === dateStr);
                if (point) {
                  factorSum += point.score;
                  factorCount++;
                }
              }
            });
            if (factorCount > 0) {
              categorySum += factorSum / factorCount;
              categoryCount++;
            }
          });
          if (categoryCount > 0) {
            dataPoint[lineKey] = Math.round(categorySum / categoryCount * 10) / 10;
          }
        });
      });
    });
    return {
      data: Array.from(dateMap.values()).sort((a, b) => a.date - b.date).map(d => {
        const {
          date,
          ...rest
        } = d;
        return {
          dateStr: new Date(date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          }),
          ...rest
        };
      }),
      lines
    };
  }, [selectedCompany, selectedRegions, selectedProjects, selectedContractors, selectedFactors, timeFrame, customStartDate, customEndDate, allProjects]);
  // Helper function to generate mini chart data for a widget
  const generateWidgetChartData = (widget: SavedWidget) => {
    const filters = widget.filters;
    let days = 30; // Default to 30 days for mini charts
    // Build list of entities
    const entities: Entity[] = [];
    if (filters.company) entities.push({
      type: 'company',
      id: 'company',
      name: 'Company Average'
    });
    filters.regions.forEach(id => entities.push({
      type: 'region',
      id,
      name: REGIONS.find(r => r.id === id)?.name || id
    }));
    filters.projects.forEach(id => entities.push({
      type: 'project',
      id,
      name: id
    }));
    filters.contractors.forEach(id => entities.push({
      type: 'contractor',
      id,
      name: id
    }));
    if (entities.length === 0 || filters.factors.length === 0) {
      return {
        data: [],
        lines: []
      };
    }
    // Separate category and individual factor selections
    const categorySelections = filters.factors.filter(f => f.startsWith('cat_'));
    const individualFactorSelections = filters.factors.filter(f => !f.startsWith('cat_'));
    const allNeededFactorIds = new Set<string>(individualFactorSelections);
    categorySelections.forEach(catKey => {
      const categoryId = catKey.replace('cat_', '');
      const category = CATEGORIES.find(c => c.id === categoryId);
      if (category) {
        category.factors.forEach(f => allNeededFactorIds.add(f.id));
      }
    });
    // Generate base time series (simplified - every 3rd day for performance)
    const projectFactorSeries = new Map<string, {
      date: string;
      score: number;
    }[]>();
    const projectFactorSeverities = new Map<string, FactorSeverity>();
    ENRICHED_MOCK_RISK_AREAS.forEach(area => {
      allNeededFactorIds.forEach(factorId => {
        const factorDef = ALL_FACTORS.find(f => f.id === factorId);
        if (!factorDef) return;
        const severity: FactorSeverity = factorDef.mapFromFactor === 'overall' ? area.riskScore >= 75 ? 'high' : area.riskScore >= 50 ? 'medium' : area.riskScore >= 25 ? 'low' : 'none' : area.riskFactorScores[factorDef.mapFromFactor];
        const key = `${area.project}_${factorId}`;
        const currentWorst = projectFactorSeverities.get(key) || 'none';
        const order = {
          high: 3,
          medium: 2,
          low: 1,
          none: 0
        };
        if (order[severity] > order[currentWorst]) {
          projectFactorSeverities.set(key, severity);
        }
      });
    });
    allProjects.forEach(project => {
      allNeededFactorIds.forEach(factorId => {
        const key = `${project}_${factorId}`;
        const severity = projectFactorSeverities.get(key) || 'none';
        projectFactorSeries.set(key, generateFactorTimeSeries(`${project}_${factorId}`, severity, 365));
      });
    });
    // Aggregate data (sample every 3rd point for mini chart)
    const dateMap = new Map<string, Record<string, number>>();
    for (let i = days; i >= 0; i -= 3) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      d.setHours(0, 0, 0, 0);
      dateMap.set(d.toISOString().split('T')[0], {
        date: d.getTime()
      });
    }
    const lines: {
      key: string;
      name: string;
      color: string;
    }[] = [];
    let colorIndex = 0;
    entities.forEach(entity => {
      individualFactorSelections.forEach(factorId => {
        const factorDef = ALL_FACTORS.find(f => f.id === factorId);
        if (!factorDef) return;
        const lineKey = `${entity.id}_${factorId}`;
        lines.push({
          key: lineKey,
          name: `${entity.name} - ${factorDef.label}`,
          color: CHART_COLORS[colorIndex % CHART_COLORS.length]
        });
        colorIndex++;
        let relevantProjects: string[] = [];
        if (entity.type === 'company') {
          relevantProjects = allProjects;
        } else if (entity.type === 'region') {
          relevantProjects = PROJECT_REGION_MAP.filter(p => p.regionId === entity.id).map(p => p.projectName);
        } else if (entity.type === 'project') {
          relevantProjects = [entity.id];
        } else if (entity.type === 'contractor') {
          relevantProjects = Array.from(new Set(ENRICHED_MOCK_RISK_AREAS.filter(a => a.contractors.includes(entity.id)).map(a => a.project)));
        }
        Array.from(dateMap.keys()).forEach(dateStr => {
          const dataPoint = dateMap.get(dateStr)!;
          let sum = 0;
          let count = 0;
          relevantProjects.forEach(project => {
            const series = projectFactorSeries.get(`${project}_${factorId}`);
            if (series) {
              const point = series.find(p => p.date === dateStr);
              if (point) {
                sum += point.score;
                count++;
              }
            }
          });
          if (count > 0) {
            dataPoint[lineKey] = Math.round(sum / count * 10) / 10;
          }
        });
      });
      categorySelections.forEach(catKey => {
        const categoryId = catKey.replace('cat_', '');
        const category = CATEGORIES.find(c => c.id === categoryId);
        if (!category) return;
        const lineKey = `${entity.id}_${catKey}`;
        lines.push({
          key: lineKey,
          name: `${entity.name} - ${category.label}`,
          color: CHART_COLORS[colorIndex % CHART_COLORS.length]
        });
        colorIndex++;
        let relevantProjects: string[] = [];
        if (entity.type === 'company') {
          relevantProjects = allProjects;
        } else if (entity.type === 'region') {
          relevantProjects = PROJECT_REGION_MAP.filter(p => p.regionId === entity.id).map(p => p.projectName);
        } else if (entity.type === 'project') {
          relevantProjects = [entity.id];
        } else if (entity.type === 'contractor') {
          relevantProjects = Array.from(new Set(ENRICHED_MOCK_RISK_AREAS.filter(a => a.contractors.includes(entity.id)).map(a => a.project)));
        }
        Array.from(dateMap.keys()).forEach(dateStr => {
          const dataPoint = dateMap.get(dateStr)!;
          let categorySum = 0;
          let categoryCount = 0;
          category.factors.forEach(subFactor => {
            let factorSum = 0;
            let factorCount = 0;
            relevantProjects.forEach(project => {
              const series = projectFactorSeries.get(`${project}_${subFactor.id}`);
              if (series) {
                const point = series.find(p => p.date === dateStr);
                if (point) {
                  factorSum += point.score;
                  factorCount++;
                }
              }
            });
            if (factorCount > 0) {
              categorySum += factorSum / factorCount;
              categoryCount++;
            }
          });
          if (categoryCount > 0) {
            dataPoint[lineKey] = Math.round(categorySum / categoryCount * 10) / 10;
          }
        });
      });
    });
    return {
      data: Array.from(dateMap.values()).sort((a, b) => a.date - b.date),
      lines
    };
  };
  // --- AI Insights Generation ---
  useEffect(() => {
    setIsAnalyzing(true);
    const timer = setTimeout(() => {
      const totalEntities = (selectedCompany ? 1 : 0) + selectedRegions.length + selectedProjects.length + selectedContractors.length;
      if (totalEntities === 0 || selectedFactors.length === 0) {
        setAiInsight('Select entities and risk factors above, or click one of the AI-suggested comparisons to begin analysis.');
        setIsAnalyzing(false);
        return;
      }
      // If an insight card is active, show its detailed AI analysis
      if (activeInsightId) {
        const activeInsight = SUGGESTED_INSIGHTS.find(i => i.id === activeInsightId);
        if (activeInsight?.aiInsight) {
          setAiInsight(activeInsight.aiInsight);
          setIsAnalyzing(false);
          return;
        }
      }
      // Generate contextual insight based on current selections
      const factorNames = selectedFactors.map(id => {
        if (id.startsWith('cat_')) {
          const categoryId = id.replace('cat_', '');
          const category = CATEGORIES.find(c => c.id === categoryId);
          return category?.label;
        }
        return ALL_FACTORS.find(f => f.id === id)?.label;
      }).filter(Boolean);
      const entityNames: string[] = [];
      if (selectedCompany) entityNames.push('Company Average');
      selectedRegions.forEach(id => {
        const r = REGIONS.find(r => r.id === id);
        if (r) entityNames.push(r.name);
      });
      selectedProjects.forEach(p => entityNames.push(p));
      selectedContractors.forEach(c => entityNames.push(c));
      const entityStr = entityNames.length > 3 ? `${entityNames.slice(0, 2).join(', ')} and ${entityNames.length - 2} others` : entityNames.join(', ');
      const factorStr = factorNames.length > 2 ? `${factorNames.slice(0, 2).join(', ')} and ${factorNames.length - 2} others` : factorNames.join(' and ');
      const tfLabel = timeFrame === '7d' ? '7 days' : timeFrame === '14d' ? '14 days' : timeFrame === '30d' ? '30 days' : timeFrame === '90d' ? '90 days' : '1 year';
      // Contextual templates that reference specific entities and factors
      const templates = [`Analyzing ${factorStr} across ${entityStr} over ${tfLabel}. The data shows diverging performance trajectories — look for crossover points where one entity's score drops below another, as these often indicate operational changes worth investigating.`, `Over the past ${tfLabel}, ${factorNames[0] || 'the primary factor'} shows the most variance among ${entityStr}. Periods where scores dip below 70 correlate with increased incident reports in historical data. Consider proactive controls when scores approach this threshold.`, `Comparing ${entityStr}: the spread in ${factorStr} scores suggests inconsistent practices across these entities. The top performer's approach to ${factorNames[0] || 'this factor'} could serve as a model — standardizing their methods could narrow the gap by an estimated 10–15 points.`, `${tfLabel} trend analysis for ${entityStr} reveals that ${factorNames[factorNames.length - 1] || 'the selected factor'} is the most volatile metric. High volatility often indicates reactive rather than preventive safety management. Recommendation: Establish weekly review cadence for entities showing >5-point swings.`];
      const seed = (selectedFactors.join('') + entityNames.join('') + timeFrame).length;
      setAiInsight(templates[seed % templates.length]);
      setIsAnalyzing(false);
    }, 800);
    return () => clearTimeout(timer);
  }, [selectedCompany, selectedRegions, selectedProjects, selectedContractors, selectedFactors, timeFrame, activeInsightId, SUGGESTED_INSIGHTS]);
  // Clear activeInsightId when user manually changes any filter
  useEffect(() => {
    // This runs on every filter change; if the user manually tweaks after clicking an insight, deactivate highlight
    if (activeInsightId) {
      const insight = SUGGESTED_INSIGHTS.find(i => i.id === activeInsightId);
      if (!insight) return;
      const f = insight.filters;
      const matches = timeFrame === f.timeFrame && selectedCompany === f.company && JSON.stringify(selectedRegions.slice().sort()) === JSON.stringify(f.regions.slice().sort()) && JSON.stringify(selectedProjects.slice().sort()) === JSON.stringify(f.projects.slice().sort()) && JSON.stringify(selectedContractors.slice().sort()) === JSON.stringify(f.contractors.slice().sort()) && JSON.stringify(selectedFactors.slice().sort()) === JSON.stringify(f.factors.slice().sort());
      if (!matches) {
        setActiveInsightId(null);
      }
    }
  }, [timeFrame, selectedCompany, selectedRegions, selectedProjects, selectedContractors, selectedFactors, activeInsightId, SUGGESTED_INSIGHTS]);
  // --- Render ---
  const totalEntities = (selectedCompany ? 1 : 0) + selectedRegions.length + selectedProjects.length + selectedContractors.length;
  // Compute responsive Y-axis domain from actual data
  const {
    yDomain,
    yTicks
  } = useMemo(() => {
    if (!chartData.data.length || !chartData.lines.length) {
      return {
        yDomain: [50, 100] as [number, number],
        yTicks: [50, 60, 70, 80, 90, 100]
      };
    }
    let min = Infinity;
    let max = -Infinity;
    chartData.data.forEach(point => {
      chartData.lines.forEach(line => {
        const val = point[line.key];
        if (typeof val === 'number') {
          if (val < min) min = val;
          if (val > max) max = val;
        }
      });
    });
    if (min === Infinity) {
      return {
        yDomain: [50, 100] as [number, number],
        yTicks: [50, 60, 70, 80, 90, 100]
      };
    }
    // Add padding and round to nice values
    const padding = Math.max((max - min) * 0.1, 2);
    const niceMin = Math.max(0, Math.floor((min - padding) / 5) * 5);
    const niceMax = Math.min(100, Math.ceil((max + padding) / 5) * 5);
    // Generate ticks
    const range = niceMax - niceMin;
    const step = range <= 15 ? 2 : range <= 30 ? 5 : 10;
    const ticks: number[] = [];
    for (let t = niceMin; t <= niceMax; t += step) {
      ticks.push(t);
    }
    if (ticks[ticks.length - 1] !== niceMax) ticks.push(niceMax);
    return {
      yDomain: [niceMin, niceMax] as [number, number],
      yTicks: ticks
    };
  }, [chartData]);
  const canSaveWidget = (selectedCompany || selectedRegions.length > 0 || selectedProjects.length > 0 || selectedContractors.length > 0) && selectedFactors.length > 0;
  const totalEntityCount = (selectedCompany ? 1 : 0) + selectedRegions.length + selectedProjects.length + selectedContractors.length;
  return <div className="h-full flex flex-col bg-gray-50/50 overflow-hidden" data-id="element-1099" data-mp="1woer9dzryd80">
      {/* Filter/Selection Bar */}
      <div className="flex-shrink-0 px-6 py-4 bg-white border-b border-gray-200 flex flex-col gap-3 z-10 shadow-sm" data-id="element-1100" data-mp="1hrea24f7m0ns">
        {/* Top Row: Timeframe + Save Widget Button */}
        <div className="flex items-center justify-between" data-id="element-1101" data-mp="134p1ze6w6f5s">
          <div className="flex items-center gap-3" data-id="element-1102" data-mp="z2lqwqo4xcl1">
            <div className="flex items-center gap-2 mr-2" data-id="element-1103" data-mp="z3wvuttbgqdq">
              <Filter size={16} className="text-teal-600" data-id="element-1104" data-mp="1d2msk8c84o1k" />
              <span className="text-sm font-semibold text-gray-700" data-id="element-1105" data-mp="1czqrib52xj8w">
                Analysis Scope:
              </span>
            </div>

            {/* Timeframe Pill Group */}
            <div className="flex bg-gray-100 p-1 rounded-lg" data-id="element-1106" data-mp="1m0yq6ubicm3i">
              {(['7d', '14d', '30d', '90d', '1y'] as TimeFrame[]).map(tf => <button key={tf} onClick={() => {
              setTimeFrame(tf);
              setShowCustomDatePicker(false);
            }} className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${timeFrame === tf ? 'bg-white text-teal-700 shadow-sm ring-1 ring-teal-200' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200/50'}`} data-id="element-1107" data-mp="1vsbejby3ku4s">
                  {tf.toUpperCase()}
                </button>)}
              <div className="relative" ref={customDateRef} data-id="element-1108" data-mp="qdn7yu31c51n">
                <button onClick={() => {
                setTimeFrame('custom');
                setShowCustomDatePicker(!showCustomDatePicker);
              }} className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-md transition-all ${timeFrame === 'custom' ? 'bg-white text-teal-700 shadow-sm ring-1 ring-teal-200' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200/50'}`} data-id="element-1109" data-mp="qg9cslyjk6g6">
                  <Calendar size={12} data-id="element-1110" data-mp="clguxgv6ie6f" />
                  {timeFrame === 'custom' ? `${new Date(customStartDate).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                })} – ${new Date(customEndDate).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                })}` : 'Custom'}
                </button>
                <AnimatePresence data-id="element-1111" data-mp="1t698693np94r">
                  {showCustomDatePicker && <motion.div initial={{
                  opacity: 0,
                  y: 4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 4
                }} transition={{
                  duration: 0.15
                }} className="absolute z-50 top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl p-4 w-[280px]" data-id="element-1112" data-mp="1o87nxkuhv3rp">
                      <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-1113" data-mp="vld8x3gg4wlm">
                        Custom Date Range
                      </div>
                      <div className="space-y-3" data-id="element-1114" data-mp="pd5mny3twrp6">
                        <div data-id="element-1115" data-mp="kcrac3v72vlc">
                          <label className="block text-[11px] font-medium text-gray-500 mb-1" data-id="element-1116" data-mp="kbzrltmp4sch">
                            Start Date
                          </label>
                          <input type="date" value={customStartDate} onChange={e => setCustomStartDate(e.target.value)} max={customEndDate} className="w-full px-3 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" data-id="element-1117" data-mp="bc2y9epd35b3" />
                        </div>
                        <div data-id="element-1118" data-mp="3jrwcfmw804oz">
                          <label className="block text-[11px] font-medium text-gray-500 mb-1" data-id="element-1119" data-mp="3jrmpk86h2dni">
                            End Date
                          </label>
                          <input type="date" value={customEndDate} onChange={e => setCustomEndDate(e.target.value)} min={customStartDate} max={new Date().toISOString().split('T')[0]} className="w-full px-3 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" data-id="element-1120" data-mp="248x30jcocjvd" />
                        </div>
                        <div className="flex items-center justify-between pt-1 border-t border-gray-100" data-id="element-1121" data-mp="1pipryoozy34p">
                          <span className="text-[11px] text-gray-400" data-id="element-1122" data-mp="1ucu3zmd4d1cn">
                            {Math.max(1, Math.round((new Date(customEndDate).getTime() - new Date(customStartDate).getTime()) / (1000 * 60 * 60 * 24)))}{' '}
                            days
                          </span>
                          <button onClick={() => setShowCustomDatePicker(false)} className="px-3 py-1 text-xs font-bold text-white bg-teal-500 hover:bg-teal-600 rounded-md transition-colors" data-id="element-1123" data-mp="29w2f5izvdagw">
                            Apply
                          </button>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Save Widget Button */}
          <div className="relative" ref={saveWidgetRef} data-id="element-1124" data-mp="36ofugjk2b9mv">
            <button onClick={() => setShowSaveWidgetPopover(!showSaveWidgetPopover)} disabled={!canSaveWidget} className={`flex items-center gap-2 px-3 py-1.5 border rounded-lg shadow-sm transition-colors text-sm font-medium ${canSaveWidget ? 'bg-white border-teal-500 text-teal-700 hover:bg-teal-50' : 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-1125" data-mp="31o1n73qac3pw">
              <Bookmark size={14} data-id="element-1126" data-mp="355pk3cq65q5a" />
              Save View as Widget
            </button>

            <AnimatePresence data-id="element-1127" data-mp="3sw336dqs0enq">
              {showSaveWidgetPopover && <motion.div initial={{
              opacity: 0,
              y: 4
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 4
            }} transition={{
              duration: 0.15
            }} className="absolute z-50 top-full right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl p-4 w-[320px]" data-id="element-1128" data-mp="1oql7wi2jx6r">
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-1129" data-mp="2nwevip404zjs">
                    Save as Widget
                  </div>
                  <div className="space-y-3" data-id="element-1130" data-mp="2izxy14awx8u1">
                    <div data-id="element-1131" data-mp="2dzjzqdz45d97">
                      <label className="block text-[11px] font-medium text-gray-500 mb-1" data-id="element-1132" data-mp="2e021tr8gilp1">
                        Widget Name
                      </label>
                      <input type="text" value={widgetName} onChange={e => setWidgetName(e.target.value)} placeholder={suggestedWidgetName} className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" autoFocus data-id="element-1133" data-mp="36no9pbopwchp" />
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-gray-500 pt-1 border-t border-gray-100" data-id="element-1134" data-mp="skd4fp3b5sl">
                      <span className="px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 font-medium" data-id="element-1135" data-mp="5s6pkmx3gi0c">
                        {timeFrame === 'custom' ? `${Math.max(1, Math.round((new Date(customEndDate).getTime() - new Date(customStartDate).getTime()) / (1000 * 60 * 60 * 24)))}D` : timeFrame.toUpperCase()}
                      </span>
                      <span className="px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 font-medium" data-id="element-1136" data-mp="op7xafi18w7z">
                        {totalEntityCount}{' '}
                        {totalEntityCount === 1 ? 'entity' : 'entities'}
                      </span>
                      <span className="px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 font-medium" data-id="element-1137" data-mp="yhvqz1dpuk2h">
                        {selectedFactors.length}{' '}
                        {selectedFactors.length === 1 ? 'factor' : 'factors'}
                      </span>
                    </div>
                    <div className="flex items-center justify-end gap-2 pt-2" data-id="element-1138" data-mp="34hdc3ewdfe79">
                      <button onClick={() => setShowSaveWidgetPopover(false)} className="text-xs font-medium text-gray-500 hover:text-gray-700" data-id="element-1139" data-mp="2zjbr2ofdx4t5">
                        Cancel
                      </button>
                      <button onClick={handleSaveWidget} className="px-3 py-1.5 text-xs font-bold text-white bg-teal-500 hover:bg-teal-600 rounded-md transition-colors" data-id="element-1140" data-mp="3n6tq950zb565">
                        Save Widget
                      </button>
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>

        {/* Bottom Row: Entity Selection */}
        <div className="flex items-center gap-3" data-id="element-1141" data-mp="1xtgit6xkl10t">
          <div className="flex items-center gap-2 mr-2" data-id="element-1142" data-mp="1xurgqq4c3ek0">
            <span className="text-sm font-semibold text-gray-700" data-id="element-1143" data-mp="1npt14q4zh13j">
              Compare Entities:
            </span>
          </div>

          <button onClick={() => setSelectedCompany(!selectedCompany)} className={`flex items-center gap-2 px-3 py-1.5 border rounded-lg shadow-sm transition-colors text-sm font-medium ${selectedCompany ? 'bg-teal-50 border-teal-500 text-teal-700 ring-1 ring-teal-500' : 'bg-white border-gray-200 text-gray-700 hover:border-teal-300'}`} data-id="element-1144" data-mp="1rsfvoyt1h04f">
            <Globe size={14} className={selectedCompany ? 'text-teal-600' : 'text-gray-400'} data-id="element-1145" data-mp="190n2p4dyzewp" />
            Company Average
          </button>

          <MultiSelect icon={<MapPin size={14} data-id="element-1147" data-mp="zzfi1p4dmcl3" />} label="Regions" options={REGIONS.map(r => ({
          value: r.id,
          label: r.name
        }))} selected={selectedRegions} onChange={setSelectedRegions} placeholder="Select Regions" data-id="element-1146" data-mp="10071y2om2lvj" />

          <MultiSelect icon={<Building2 size={14} data-id="element-1149" data-mp="1tev3p102pbr" />} label="Projects" options={allProjects.map(p => ({
          value: p,
          label: p
        }))} selected={selectedProjects} onChange={setSelectedProjects} placeholder="Select Projects" data-id="element-1148" data-mp="6uc8zpez8x7g" />

          <MultiSelect icon={<Users size={14} data-id="element-1151" data-mp="3k8pekdj780l3" />} label="Contractors" options={allContractors.map(c => ({
          value: c,
          label: c
        }))} selected={selectedContractors} onChange={setSelectedContractors} placeholder="Select Contractors" data-id="element-1150" data-mp="3k7nszf4k3vp4" />

          <MultiSelect icon={<AlertTriangle size={14} data-id="element-1153" data-mp="18hbnlurz8yh5" />} label="Risk Factors" options={CATEGORIES.flatMap(category => [{
          value: `cat_${category.id}`,
          label: `All ${category.label}`,
          group: category.label
        }, ...category.factors.map(factor => ({
          value: factor.id,
          label: factor.label,
          group: category.label
        }))])} selected={selectedFactors} onChange={setSelectedFactors} placeholder="Select Risk Factors" data-id="element-1152" data-mp="369g0pkigtika" />
        </div>

        {/* AI Suggested Comparisons — Chips */}
        <div className="flex items-center gap-2 flex-wrap" data-id="element-1154" data-mp="28dkdgjycqtyd">
          <div className="flex items-center gap-1.5 mr-1" data-id="element-1155" data-mp="28e3xc67kbsf1">
            <Sparkles size={12} className="text-teal-500" data-id="element-1156" data-mp="2cf5w6lxki2gh" />
            <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider" data-id="element-1157" data-mp="2chs2ki8q2bx4">
              Suggested:
            </span>
          </div>
          {SUGGESTED_INSIGHTS.map(insight => {
          const isActive = activeInsightId === insight.id;
          return <div key={insight.id} className="relative group/tip" data-id="element-1158" data-mp="3svrzrlg6nngo">
                <button onClick={() => applyInsight(insight)} className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all ${isActive ? 'bg-teal-600 border-teal-600 text-white shadow-sm' : 'bg-white border-gray-200 text-gray-600 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700'}`} data-id="element-1159" data-mp="nrf6ani9iakw">
                  {isActive && <Check size={10} data-id="element-1160" data-mp="4y1lhb89jkj0" />}
                  <span className="max-w-[200px] truncate" data-id="element-1161" data-mp="917yd1pvs9yg">
                    {insight.title}
                  </span>
                </button>
                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 z-50 hidden group-hover/tip:block pointer-events-none" data-id="element-1162" data-mp="3r7umulwsvd4v">
                  <div className="bg-gray-900 text-white text-[11px] leading-relaxed rounded-lg px-3 py-2.5 shadow-xl max-w-[280px] whitespace-normal" data-id="element-1163" data-mp="2ctpu2hj7x9">
                    <div className="font-bold mb-1" data-id="element-1164" data-mp="1udhtuoed527u">{insight.title}</div>
                    <div className="text-gray-300" data-id="element-1165" data-mp="3ewbrfnqauyit">{insight.description}</div>
                    <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-gray-900" data-id="element-1166" data-mp="3eu8x4ri46j8a" />
                  </div>
                </div>
              </div>;
        })}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-5" data-id="element-1167" data-mp="1gvxayc8a4sf">
        {/* Top Row: Chart + AI Insight Sidebar */}
        <div className="flex gap-5 flex-1 min-h-[420px]" data-id="element-1168" data-mp="3sngkg6z5wsk6">
          {/* Chart Area */}
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          ease: 'easeOut'
        }} className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex-1 flex flex-col min-w-0" data-id="element-1169" data-mp="bejwq1ks40gx">
            <div className="mb-4 flex items-center justify-between" data-id="element-1170" data-mp="2xxv9nqwuc56p">
              <div className="flex items-center gap-2" data-id="element-1171" data-mp="31yx7q4f0ub73">
                <h3 className="text-lg font-bold text-gray-900" data-id="element-1172" data-mp="31zp1d9p25yuq">
                  Comparative Risk Trends
                </h3>
                <span className="px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 text-[10px] font-bold uppercase tracking-wider" data-id="element-1173" data-mp="3bwl57240hwsy">
                  {timeFrame.toUpperCase()}
                </span>
              </div>
            </div>

            <div className="flex-1 min-h-0" data-id="element-1174" data-mp="3kv6t7cs96414">
              {chartData.data.length > 0 && chartData.lines.length > 0 ? <ResponsiveContainer width="100%" height="100%" data-id="element-1175" data-mp="3ppje7r9dnnez">
                  <LineChart data={chartData.data} margin={{
                top: 10,
                right: 20,
                left: -20,
                bottom: 0
              }} data-id="element-1176" data-mp="3ul88bf2moc3w">
                    <defs data-id="element-1177" data-mp="1sxnil9820dga">
                      {chartData.lines.map(line => <linearGradient key={`grad-${line.key}`} id={`grad-${line.key}`} x1="0" y1="0" x2="0" y2="1" data-id="element-1178" data-mp="1nykb7i4lpv55">
                          <stop offset="5%" stopColor={line.color} stopOpacity={0.3} data-id="element-1179" data-mp="2bnlp3kw7dyht" />
                          <stop offset="95%" stopColor={line.color} stopOpacity={0} data-id="element-1180" data-mp="21xsg8rng3xh9" />
                        </linearGradient>)}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" data-id="element-1181" data-mp="2pnmp6y4n91w8" />
                    <XAxis dataKey="dateStr" axisLine={false} tickLine={false} tick={{
                  fontSize: 12,
                  fill: '#9ca3af'
                }} dy={10} minTickGap={30} data-id="element-1182" data-mp="2a7pr2aeghdc8" />
                    <YAxis domain={yDomain} axisLine={false} tickLine={false} tick={{
                  fontSize: 12,
                  fill: '#9ca3af'
                }} ticks={yTicks} data-id="element-1183" data-mp="2sxfluxi7xz45" />
                    <Tooltip content={({
                  active,
                  payload,
                  label
                }) => {
                  if (active && payload && payload.length) {
                    return <div className="bg-slate-900/90 backdrop-blur-md border border-slate-700 p-4 rounded-xl shadow-xl text-white min-w-[200px]" data-id="element-1185" data-mp="3gnjpv5zbk0vo">
                              <p className="text-slate-400 text-xs font-semibold mb-3 uppercase tracking-wider" data-id="element-1186" data-mp="106lmcnfiml37">
                                {label}
                              </p>
                              <div className="flex flex-col gap-2" data-id="element-1187" data-mp="19x6kyav6vpm4">
                                {payload.map((entry: any, index: number) => <div key={index} className="flex items-center justify-between gap-4 text-sm" data-id="element-1188" data-mp="m9yhlyhbmqmq" data-mpi={index}>
                                    <div className="flex items-center gap-2" data-id="element-1189" data-mp="r6yp8dttk1fy">
                                      <div className="w-2 h-2 rounded-full" style={{
                              backgroundColor: entry.color
                            }} data-id="element-1190" data-mp="r91j58y3eepy" />
                                      <span className="text-slate-200 font-medium truncate max-w-[180px]" data-id="element-1191" data-mp="14ab155gihi9g">
                                        {entry.name}
                                      </span>
                                    </div>
                                    <span className="font-bold" data-id="element-1192" data-mp="1d9oc62kaaewa">
                                      {typeof entry.value === 'number' ? entry.value.toFixed(1) : entry.value}
                                    </span>
                                  </div>)}
                              </div>
                            </div>;
                  }
                  return null;
                }} cursor={{
                  stroke: '#cbd5e1',
                  strokeWidth: 1,
                  strokeDasharray: '4 4'
                }} data-id="element-1184" data-mp="2o31legop91sm" />

                    {chartData.lines.map(line => {
                  const isHighlighted = highlightedKeys?.has(line.key) ?? false;
                  const isDimmed = highlightedKeys && !highlightedKeys.has(line.key);
                  return <Line key={line.key} type="natural" dataKey={line.key} name={line.name} stroke={line.color} strokeWidth={isHighlighted ? 4 : isDimmed ? 1.5 : 2.5} strokeLinecap="round" strokeOpacity={isDimmed ? 0.2 : 1} dot={false} activeDot={{
                    r: isHighlighted ? 7 : 5,
                    stroke: '#fff',
                    strokeWidth: 3,
                    fill: line.color,
                    style: {
                      filter: `drop-shadow(0px 0px ${isHighlighted ? '6' : '4'}px ${line.color}80)`
                    }
                  }} style={{
                    transition: 'stroke-width 200ms ease, stroke-opacity 200ms ease',
                    filter: isHighlighted ? `drop-shadow(0px 0px 6px ${line.color}60)` : 'none'
                  }} data-id="element-1193" data-mp="37jczig8odsxg" />;
                })}
                  </LineChart>
                </ResponsiveContainer> : <div className="h-full flex flex-col items-center justify-center text-gray-400" data-id="element-1194" data-mp="361mdfikd0zjk">
                  <div className="w-16 h-16 mb-4 rounded-full bg-gray-50 flex items-center justify-center border border-gray-100" data-id="element-1195" data-mp="3aymk9vp1a6bq">
                    <LineChartIcon size={28} className="text-gray-300" data-id="element-1196" data-mp="2re4gx7gybb88" />
                  </div>
                  <p className="text-base font-semibold text-gray-600" data-id="element-1197" data-mp="2mgm6fcnacny4">
                    Ready for Analysis
                  </p>
                  <p className="text-sm mt-1 max-w-sm text-center" data-id="element-1198" data-mp="2wcz0wcqnn5tx">
                    Select entities and risk factors above, or click a
                    suggestion below.
                  </p>
                </div>}
            </div>

            {/* External Legend — fixed max-height, scrollable */}
            {chartData.lines.length > 0 && <div className="flex-shrink-0 mt-3 pt-3 border-t border-gray-100" data-id="element-1199" data-mp="3k1ryedmxdlqn">
                <div className="max-h-[72px] overflow-y-auto overflow-x-hidden" data-id="element-1200" data-mp="3f4rrk0i94eyh">
                  <div className="flex flex-wrap items-center justify-center gap-1.5" data-id="element-1201" data-mp="3t2gav3wo7e1f">
                    {chartData.lines.map((line, index) => {
                  const lineKey = line.key;
                  const isPinned = pinnedLineKeys.has(lineKey);
                  const isHighlighted = highlightedKeys?.has(lineKey) ?? false;
                  const isDimmed = highlightedKeys && !highlightedKeys.has(lineKey);
                  return <div key={`legend-${index}`} onMouseEnter={() => setHoveredLineKey(lineKey)} onMouseLeave={() => setHoveredLineKey(null)} onClick={() => {
                    setPinnedLineKeys(prev => {
                      const next = new Set(prev);
                      if (next.has(lineKey)) {
                        next.delete(lineKey);
                      } else {
                        next.add(lineKey);
                      }
                      return next;
                    });
                  }} className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full border transition-all duration-200 cursor-pointer select-none ${isPinned ? 'bg-gray-900 border-gray-700 shadow-md ring-1 ring-gray-600' : isHighlighted ? 'bg-gray-100 border-gray-300 shadow-sm' : isDimmed ? 'bg-gray-50 border-gray-100 opacity-40' : 'bg-gray-50 border-gray-100 hover:bg-gray-100 hover:border-gray-200'}`} data-id="element-1202" data-mp="q7nbbtxn6ez1" data-mpi={index}>
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 transition-transform duration-200 ${isHighlighted ? 'scale-125' : ''}`} style={{
                      backgroundColor: line.color
                    }} data-id="element-1203" data-mp="1nllh7w6db0ss" />
                          <span className={`text-[10px] font-medium transition-colors duration-200 truncate max-w-[160px] ${isPinned ? 'text-white' : isHighlighted ? 'text-gray-900' : 'text-gray-600'}`} data-id="element-1204" data-mp="zx28xgnbio0p">
                            {line.name}
                          </span>
                        </div>;
                })}
                  </div>
                </div>
              </div>}
          </motion.div>

          {/* AI Insight Sidebar */}
          <motion.div layout initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.4,
          delay: 0.1
        }} className="w-[320px] flex-shrink-0 bg-gradient-to-b from-teal-900 to-teal-800 rounded-xl shadow-lg text-white relative overflow-hidden flex flex-col" data-id="element-1205" data-mp="6zhy8ykvwo0p">
            {/* Decorative background */}
            <div className="absolute top-0 right-0 -mt-10 -mr-10 w-48 h-48 bg-teal-500 rounded-full opacity-10 blur-3xl" data-id="element-1206" data-mp="3aib8p2id8wqn" />
            <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-40 h-40 bg-emerald-400 rounded-full opacity-10 blur-2xl" data-id="element-1207" data-mp="1q120ledvzt6o" />

            <div className="relative z-10 p-5 flex flex-col h-full" data-id="element-1208" data-mp="1uzwtj95kiy4i">
              <div className="flex items-center gap-3 mb-4" data-id="element-1209" data-mp="1zy6p3kv7g0qn">
                <div className="w-9 h-9 rounded-full bg-teal-700/50 flex items-center justify-center border border-teal-500/30 shadow-inner" data-id="element-1210" data-mp="1zwn6vopznfm8">
                  <Sparkles size={18} className="text-teal-300" data-id="element-1211" data-mp="29p1fn1fhhmhw" />
                </div>
                <div data-id="element-1212" data-mp="2dpbrgupyj74o">
                  <h3 className="text-sm font-bold text-teal-50 tracking-wide" data-id="element-1213" data-mp="2dojxtpfx7jh1">
                    BiltonAI Insights
                  </h3>
                  <span className="text-[9px] font-bold text-teal-300/60 uppercase tracking-wider" data-id="element-1214" data-mp="2in4z7s6ws972">
                    Live Analysis
                  </span>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto" data-id="element-1215" data-mp="od9qj47doou5">
                <AnimatePresence mode="wait" data-id="element-1216" data-mp="tat2fxglspix">
                  {isAnalyzing ? <motion.div key="analyzing" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} className="flex items-center gap-2 text-teal-200/80 text-sm font-medium" data-id="element-1217" data-mp="2jqc3qzw8k7fy">
                      <div className="flex gap-1.5" data-id="element-1218" data-mp="1w1j68yzytrj8">
                        <div className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-bounce" style={{
                      animationDelay: '0ms'
                    }} data-id="element-1219" data-mp="1w2uat118j3be" />
                        <div className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-bounce" style={{
                      animationDelay: '150ms'
                    }} data-id="element-1220" data-mp="1hc4u5j508w05" />
                        <div className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-bounce" style={{
                      animationDelay: '300ms'
                    }} data-id="element-1221" data-mp="1351ikdrqh9tv" />
                      </div>
                      Analyzing...
                    </motion.div> : <motion.p key="insight" initial={{
                  opacity: 0,
                  y: 5
                }} animate={{
                  opacity: 1,
                  y: 0
                }} className="text-teal-50/90 text-sm leading-relaxed font-medium" data-id="element-1222" data-mp="1ly5e64oihwr4">
                      {aiInsight}
                    </motion.p>}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </div>

        {/* My Widgets Dashboard */}
        {savedWidgets.length > 0 && <motion.div ref={widgetsSectionRef} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-id="element-1223" data-mp="2389cxb5k9b5u">
            <div className="flex items-center justify-between mb-4" data-id="element-1224" data-mp="1kfds96lchjh5">
              <div className="flex items-center gap-2" data-id="element-1225" data-mp="1fh3v4qg288sw">
                <LayoutGrid size={18} className="text-teal-600" data-id="element-1226" data-mp="1fhm14esiy1e0" />
                <h3 className="text-lg font-bold text-gray-900" data-id="element-1227" data-mp="1ajat89lsi0yf">My Widgets</h3>
                <span className="px-2 py-0.5 rounded-md bg-teal-100 text-teal-700 text-[10px] font-bold" data-id="element-1228" data-mp="39vwrirorp3lw">
                  {savedWidgets.length}
                </span>
              </div>
              <button onClick={() => {
            if (window.confirm('Are you sure you want to delete all saved widgets?')) {
              setSavedWidgets([]);
            }
          }} className="text-xs font-medium text-gray-500 hover:text-red-600 transition-colors" data-id="element-1229" data-mp="3cg2v2y9gys1x">
                Clear All
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1230" data-mp="2yg1wxqkl9yq5">
              {savedWidgets.map(widget => {
            const widgetChartData = generateWidgetChartData(widget);
            const entityCount = (widget.filters.company ? 1 : 0) + widget.filters.regions.length + widget.filters.projects.length + widget.filters.contractors.length;
            return <motion.div key={widget.id} initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.95
            }} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer group" onClick={() => handleLoadWidget(widget)} data-id="element-1231" data-mp="nq3clf8g4rxj">
                    <div className="flex items-start justify-between mb-3" data-id="element-1232" data-mp="21h8cmbphtpsh">
                      <h4 className="text-sm font-bold text-gray-900 truncate flex-1 pr-2" data-id="element-1233" data-mp="1whlym01uc5im">
                        {widget.name}
                      </h4>
                      <button onClick={e => {
                  e.stopPropagation();
                  handleDeleteWidget(widget.id);
                }} className="flex-shrink-0 text-gray-400 hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100" data-id="element-1234" data-mp="737exu5oj8tz">
                        <Trash2 size={14} data-id="element-1235" data-mp="241o9ofug83iy" />
                      </button>
                    </div>

                    <div className="flex items-center gap-1.5 mb-3 flex-wrap" data-id="element-1236" data-mp="28zpogokudgom">
                      <span className="px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 text-[10px] font-medium" data-id="element-1237" data-mp="1v0gazwo0t0oc">
                        {widget.filters.timeFrame === 'custom' ? `${Math.max(1, Math.round((new Date(widget.filters.customEndDate!).getTime() - new Date(widget.filters.customStartDate!).getTime()) / (1000 * 60 * 60 * 24)))}D` : widget.filters.timeFrame.toUpperCase()}
                      </span>
                      <span className="px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 text-[10px] font-medium" data-id="element-1238" data-mp="3fh33xbv9whad">
                        {entityCount}{' '}
                        {entityCount === 1 ? 'entity' : 'entities'}
                      </span>
                      <span className="px-2 py-0.5 rounded-md bg-gray-100 text-gray-600 text-[10px] font-medium" data-id="element-1239" data-mp="tjpvd9x1f3x6">
                        {widget.filters.factors.length}{' '}
                        {widget.filters.factors.length === 1 ? 'factor' : 'factors'}
                      </span>
                    </div>

                    <div className="h-[120px] mb-3" data-id="element-1240" data-mp="18bgisk393bkc">
                      {widgetChartData.data.length > 0 && widgetChartData.lines.length > 0 ? <ResponsiveContainer width="100%" height="100%" data-id="element-1241" data-mp="1habprye4cpop">
                          <LineChart data={widgetChartData.data} margin={{
                    top: 5,
                    right: 5,
                    left: 5,
                    bottom: 5
                  }} data-id="element-1242" data-mp="1m7u11vflzgzv">
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" data-id="element-1243" data-mp="3gik2ynk85zni" />
                            {widgetChartData.lines.map(line => <Line key={line.key} type="natural" dataKey={line.key} stroke={line.color} strokeWidth={2} dot={false} data-id="element-1244" data-mp="31prohzglouwz" />)}
                          </LineChart>
                        </ResponsiveContainer> : <div className="h-full flex items-center justify-center text-gray-300 text-xs" data-id="element-1245" data-mp="2e3l4sfxtdqqx">
                          No data
                        </div>}
                    </div>

                    <button onClick={e => {
                e.stopPropagation();
                handleLoadWidget(widget);
              }} className="w-full text-xs font-medium text-teal-600 hover:text-teal-700 py-1.5 border-t border-gray-100 transition-colors" data-id="element-1246" data-mp="2bfzoveatp7d6">
                      Load View
                    </button>
                  </motion.div>;
          })}
            </div>
          </motion.div>}
      </div>
    </div>;
}